/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React from "react";

/*
 * The one tooltip every chart uses (look: styles/tooltips.css, `.gh-tip`). Pass it to recharts through
 * `chartTooltip()`:  <Tooltip {...chartTooltip({ formatter, labelFormatter })} />
 */

interface TipEntry {
  name?: string | number;
  value?: number | string | Array<number | string>;
  color?: string;
  stroke?: string;
  fill?: string;
  dataKey?: string | number;
  payload?: unknown;
  hide?: boolean;
}

export type TipFormatter = (value: number | string, name: string, entry: TipEntry) => string | [React.ReactNode, React.ReactNode];

interface ChartTooltipProps {
  // injected by recharts
  active?: boolean;
  payload?: TipEntry[];
  label?: unknown;
  // ours
  formatter?: TipFormatter;
  labelFormatter?: (label: unknown, payload: TipEntry[]) => React.ReactNode;
  /** rows whose value is exactly 0 are left out (stacked charts with empty series) */
  hideZero?: boolean;
}

export const ChartTooltip: React.FC<ChartTooltipProps> = ({ active, payload, label, formatter, labelFormatter, hideZero }) => {
  if (!active || !payload || payload.length === 0) return null;

  const rows = payload
    .filter((p) => !p.hide && p.value !== undefined && p.value !== null && !(hideZero && p.value === 0))
    .map((p, i) => {
      const rawName = String(p.name ?? p.dataKey ?? "");
      const raw = Array.isArray(p.value) ? p.value[p.value.length - 1] : (p.value as number | string);
      let name: React.ReactNode = rawName;
      let value: React.ReactNode = raw;
      if (formatter) {
        const out = formatter(raw, rawName, p);
        if (Array.isArray(out)) {
          value = out[0];
          name = out[1];
        } else {
          value = out;
        }
      }
      return { key: `${rawName}-${i}`, name, value, color: p.color ?? p.stroke ?? p.fill ?? "var(--gh-signal)" };
    });
  if (rows.length === 0) return null;

  const head = labelFormatter ? labelFormatter(label, payload) : label;
  return (
    <div className="gh-tip" role="status">
      {head !== undefined && head !== null && head !== "" && <div className="gh-tip-head">{head}</div>}
      {rows.map((r) => (
        <div key={r.key} className="gh-tip-row">
          <i style={{ background: r.color }} />
          <span>{r.name}</span>
          <b>{r.value}</b>
        </div>
      ))}
    </div>
  );
};

/** Props for recharts' <Tooltip>: our content, a quiet crosshair, no animation lag. */
export function chartTooltip(opts: Omit<ChartTooltipProps, "active" | "payload" | "label"> = {}) {
  return {
    content: <ChartTooltip {...opts} />,
    cursor: { stroke: "var(--gh-mute)", strokeWidth: 1, strokeDasharray: "3 3" },
    isAnimationActive: false,
    offset: 14,
    wrapperStyle: { outline: "none", zIndex: 50 },
  } as const;
}
